import React from "react";
import {
  WSButton,
  WSControl,
  WSDivider,
  WSElement,
  WSFlexBox,
  WSForm,
  WSInputPassword,
  WSInputText,
  WSList,
  WSText
} from "@wingspanhq/fe-component-library";
import * as Yup from "yup";
import { PersonInviteRedemption } from "@wingspanhq/wingspan-api-sdk";
import { useHistory } from "react-router-dom";
import { validatorEmail } from "../../../../shared/validators/validatorEmail";
import { validatorPassword } from "../../../../shared/validators/validatorPassword";
import { InvitationAuthPrivacyPolicyInfoBlock } from "../InvitationAuthPrivacyPolicyInfoBlock";
import {
  CompleteRegistrationData,
  useMutationCompleteRegistration
} from "../../mutations/useMutationCompleteRegistration";
import { GoogleSignIn } from "../GoogleSignIn";
import { useWSAnalytics } from "../../../../utils/WSAnalytics";
import { useSetWSStore } from "../../../../store";
import { WSErrorMessage } from "../../../../components/WSErrorMessage/WSErrorMessage";
import { useNotYourEmailPayee } from "../../modals/useNotYourEmailPayee";
import { INVITATION_AUTH_SUCCESS_PATH } from "../../index";

type Props = {
  redemption: PersonInviteRedemption;
};

export const InvitationAuthSignUpForm: React.FC<Props> = ({ redemption }) => {
  const [submit, submitMeta] = useMutationCompleteRegistration(redemption);
  const notYourEmailPayeeModal = useNotYourEmailPayee();
  const history = useHistory();
  const analytics = useWSAnalytics();
  const setStore = useSetWSStore();

  return (
    <>
      <WSList gap="XL" p="XL">
        <WSText.Paragraph weight="medium">Sign Up</WSText.Paragraph>

        <WSForm<CompleteRegistrationData>
          onSubmit={async values => {
            await submit(values, {
              onSuccess() {
                setStore({ signUpMethod: "Email" });
                analytics.track.signUp();
                history.replace(INVITATION_AUTH_SUCCESS_PATH);
              }
            });
          }}
          defaultValues={{
            email: redemption.email || "",
            firstName: "",
            lastName: "",
            password: "",
            remember: true
          }}
          validationSchema={Yup.object().shape({
            email: validatorEmail.required("Email is required"),
            firstName: Yup.string().required("First name is required"),
            lastName: Yup.string().required("Last name is required"),
            password: validatorPassword.required("Password is required"),
            remember: Yup.boolean()
          })}
        >
          <WSForm.Field
            mb="L"
            name="email"
            label="Email"
            description="This email was provided by your inviter and will be used to sign in to your account."
            component={WSInputText}
            componentProps={{
              disabled: !!redemption.email,
              required: true,
              type: "email",
              autoComplete: "email"
            }}
          />
          <WSForm.Field
            mb="L"
            name="firstName"
            label="First name"
            component={WSInputText}
            componentProps={{
              required: true,
              autoComplete: "given-name"
            }}
          />
          <WSForm.Field
            mb="L"
            name="lastName"
            label="Last name"
            component={WSInputText}
            componentProps={{
              required: true,
              autoComplete: "family-name"
            }}
          />
          <WSForm.Field
            mb="L"
            name="password"
            label="Create password"
            component={WSInputPassword}
            componentProps={{
              required: true,
              autoComplete: "new-password"
            }}
          />
          <WSForm.Field
            name="remember"
            component={WSControl}
            componentProps={{
              size: "S",
              type: "checkbox",
              label: "Stay signed in"
            }}
          />

          <InvitationAuthPrivacyPolicyInfoBlock mt="XL" mb="L" />

          <WSErrorMessage
            my="XL"
            contextKey="Auth"
            forceShowApiErrors
            error={submitMeta.error}
          />

          <WSForm.SubmitButton fullWidth size="M">
            Get started with Wingspan
          </WSForm.SubmitButton>
        </WSForm>
      </WSList>
      <WSDivider label="or" />

      <WSElement p="XL">
        <GoogleSignIn title="Sign up with Google" />
      </WSElement>

      <WSDivider />

      <WSFlexBox p="XL" justify="space-between">
        <WSButton.Link
          size="S"
          rightIcon="chevron-right"
          onClick={async () => {
            await notYourEmailPayeeModal.open({
              email: redemption.email || ""
            });
          }}
        >
          Not your email?
        </WSButton.Link>
      </WSFlexBox>
    </>
  );
};
